import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import MainLayout from "../components/layout/MainLayout";
import InquiryList from "./Inquiry/InquiryList";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from 'sweetalert2';


import {
    fetchInquiries,
    deleteInquiry,
    clearError,
    enrollInquiryAsPatient, //new import
} from "../store/slices/inquirySlice";
import { selectHasPermission } from "../store/slices/authSlice";

const Inquiry = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { inquiries, isLoading, error } = useSelector(
        (state) => state.inquiries
    );

    // ✅ Permission checks
    const canManageInquiries = useSelector((state) =>
        selectHasPermission(state, "inquiries-manage")
    );
    const canCreateInquiries = useSelector((state) =>
        selectHasPermission(state, "inquiries-create")
    );
    const canEditInquiries = useSelector((state) =>
        selectHasPermission(state, "inquiries-edit")
    );
    const canDeleteInquiries = useSelector((state) =>
        selectHasPermission(state, "inquiries-delete")
    );
    const canEnrollInquiries = useSelector((state) =>
        selectHasPermission(state, "inquiries-enroll")
    );

    // ✅ Fetch inquiries
    useEffect(() => {
        if (canManageInquiries) {
            dispatch(fetchInquiries());
        }
    }, [dispatch, canManageInquiries]);

    // ✅ Handle errors
    useEffect(() => {
        if (error) console.error("Inquiry error:", error);
    }, [error]);

    // ✅ No permission redirect
    if (!canManageInquiries) {
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage inquiries." }}
                replace
            />
        );
    }

    // ✅ Action handlers

    const handleAddInquiry = () => navigate("/inquiry/add");
    const handleEditInquiry = (id) => navigate(`/inquiry/edit/${id}`);


    const handleDeleteInquiry = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You want to delete this inquiry?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteInquiry(id)).unwrap();
                dispatch(fetchInquiries());
                Swal.fire('Deleted!', 'Inquiry has been deleted.', 'success');
            } catch (err) {
                console.error("Failed to delete inquiry:", err);
                Swal.fire('Error!', 'Failed to delete inquiry.', 'error');
            }
        }
    };

    const handleEnrollInquiry = async (id) => {
        try {
            // 🟡 Step 1: Ask for confirmation first
            const confirmResult = await Swal.fire({
                title: "Confirm Enrollment",
                text: "Are you sure you want to enroll this inquiry as a patient and a case?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Enroll",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                position: "top",
                width: "350px",
            });

            // 🟢 Step 2: If user cancels, stop here
            if (!confirmResult.isConfirmed) {
                return;
            }

            // 🟢 Step 3: Proceed only if confirmed
            const result = await dispatch(enrollInquiryAsPatient(id)).unwrap();

            // 🟢 Step 4: Check if already enrolled
            const message = result?.message?.toLowerCase() || "";
            if (message.includes("already enrolled")) {
                Swal.fire({
                    title: "Notice",
                    text: "This inquiry is already enrolled as a patient and case.",
                    icon: "info",
                    confirmButtonText: "OK",
                    position: "top",
                    width: "300px",
                });
                return; // stop here — don't show success
            }

            // 🟢 Step 5: Otherwise show success
            Swal.fire({
                title: "Success!",
                text: result.message || "Enrolled as patient and case successfully.",
                icon: "success",
                confirmButtonText: "OK",
                timer: 2000,
                position: "top",
                width: "300px",
            });

            // Optionally refresh inquiries
            dispatch(fetchInquiries());
        } catch (err) {
            console.error("Enroll failed:", err);

            Swal.fire({
                title: "Notice",
                text:
                    err?.message ||
                    err?.error ||
                    "Already enrolled as patient or something went wrong.",
                icon: "info",
                confirmButtonText: "OK",
                position: "top",
                width: "300px",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {/* ===== Error Alert ===== */}
                {error && (
                    <div
                        className="alert alert-danger alert-dismissible fade show"
                        role="alert"
                    >
                        <strong>Error:</strong> {error}
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => dispatch(clearError())}
                        ></button>
                    </div>
                )}

                {/* ===== Inquiry Table ===== */}
                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <InquiryList
                                inquiries={inquiries}
                                onEdit={canEditInquiries ? handleEditInquiry : null}
                                onDelete={canDeleteInquiries ? handleDeleteInquiry : null}
                                onAddInquiry={canCreateInquiries ? handleAddInquiry : null}
                                onEnroll={canEnrollInquiries ? handleEnrollInquiry : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default Inquiry;
